import React, { useEffect, useState, useMemo } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Plus, X, Ban } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const emptyLinea = () => ({ cuenta_id: "", glosa: "", debito: "", credito: "" });

export default function LibroDiario() {
  const [asientos, setAsientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);

  const [fecha, setFecha] = useState(todayISO());
  const [glosa, setGlosa] = useState("");
  const [lineas, setLineas] = useState([emptyLinea(), emptyLinea()]);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([api.get("/asientos"), api.get("/cuentas")]);
      setAsientos(a.data);
      setCuentas(c.data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cuentasImputables = useMemo(
    () => cuentas.filter((c) => c.imputable).sort((a, b) => a.codigo.localeCompare(b.codigo)),
    [cuentas]
  );

  const totalDebito = lineas.reduce((s, l) => s + (parseFloat(l.debito) || 0), 0);
  const totalCredito = lineas.reduce((s, l) => s + (parseFloat(l.credito) || 0), 0);
  const balanced = totalDebito > 0 && Math.round(totalDebito) === Math.round(totalCredito);

  const updateLinea = (i, patch) => {
    const arr = [...lineas];
    arr[i] = { ...arr[i], ...patch };
    setLineas(arr);
  };

  const addLinea = () => setLineas([...lineas, emptyLinea()]);
  const removeLinea = (i) => {
    if (lineas.length <= 2) return;
    setLineas(lineas.filter((_, idx) => idx !== i));
  };

  const reset = () => {
    setFecha(todayISO());
    setGlosa("");
    setLineas([emptyLinea(), emptyLinea()]);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      fecha,
      glosa,
      lineas: lineas
        .filter((l) => l.cuenta_id)
        .map((l) => ({
          cuenta_id: l.cuenta_id,
          glosa: l.glosa || "",
          debito: parseFloat(l.debito) || 0,
          credito: parseFloat(l.credito) || 0,
        })),
    };
    try {
      await api.post("/asientos", payload);
      toast.success("Asiento registrado");
      setOpenDialog(false);
      reset();
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const anular = async (id) => {
    if (!window.confirm("¿Anular este asiento?")) return;
    try {
      await api.post(`/asientos/${id}/anular`);
      toast.success("Asiento anulado");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <Page>
      <PageHeader
        title="Libro diario"
        subtitle="Asientos contables — débito y crédito balanceados"
        testid="libro-diario-header"
        actions={
          <Dialog open={openDialog} onOpenChange={(v) => { setOpenDialog(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
              <button
                data-testid="new-asiento-button"
                className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-3 py-2 rounded-sm"
              >
                <Plus className="h-4 w-4" /> Nuevo asiento
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Nuevo asiento contable</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} data-testid="new-asiento-form">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="label-caps block mb-1">Fecha</label>
                    <input
                      type="date"
                      required
                      data-testid="asiento-fecha"
                      value={fecha}
                      onChange={(e) => setFecha(e.target.value)}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/30 text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="label-caps block mb-1">Glosa</label>
                    <input
                      required
                      data-testid="asiento-glosa"
                      value={glosa}
                      onChange={(e) => setGlosa(e.target.value)}
                      placeholder="ej: Venta del día / Pago arriendo"
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/30 text-sm"
                    />
                  </div>
                </div>

                <div className="border border-[#E2DFD8] rounded-sm overflow-hidden">
                  <table className="acc-table">
                    <thead>
                      <tr>
                        <th>Cuenta</th>
                        <th>Glosa</th>
                        <th className="num" style={{ width: 130 }}>Débito</th>
                        <th className="num" style={{ width: 130 }}>Crédito</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map((l, i) => (
                        <tr key={i}>
                          <td>
                            <Select value={l.cuenta_id} onValueChange={(v) => updateLinea(i, { cuenta_id: v })}>
                              <SelectTrigger data-testid={`linea-cuenta-${i}`} className="h-8 text-xs">
                                <SelectValue placeholder="Seleccionar..." />
                              </SelectTrigger>
                              <SelectContent>
                                {cuentasImputables.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    <span className="font-mono mr-2">{c.codigo}</span>{c.nombre}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td>
                            <input
                              value={l.glosa}
                              onChange={(e) => updateLinea(i, { glosa: e.target.value })}
                              data-testid={`linea-glosa-${i}`}
                              className="w-full px-2 py-1 border border-[#E2DFD8] rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-[#1B4332]/30"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={l.debito}
                              onChange={(e) => updateLinea(i, { debito: e.target.value, credito: e.target.value ? "" : l.credito })}
                              data-testid={`linea-debito-${i}`}
                              className="w-full px-2 py-1 border border-[#E2DFD8] rounded-sm text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-[#1B4332]/30"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={l.credito}
                              onChange={(e) => updateLinea(i, { credito: e.target.value, debito: e.target.value ? "" : l.debito })}
                              data-testid={`linea-credito-${i}`}
                              className="w-full px-2 py-1 border border-[#E2DFD8] rounded-sm text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-[#1B4332]/30"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removeLinea(i)}
                              data-testid={`remove-linea-${i}`}
                              className="text-[#A94438] hover:bg-[#A94438]/10 p-1 rounded-sm"
                              disabled={lineas.length <= 2}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#F9F8F6] font-semibold">
                        <td colSpan={2} className="text-right pr-3 text-xs label-caps">Totales</td>
                        <td className="num text-[#2D6A4F]" data-testid="total-debito">{fmtCLP(totalDebito)}</td>
                        <td className="num text-[#A94438]" data-testid="total-credito">{fmtCLP(totalCredito)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <button
                    type="button"
                    onClick={addLinea}
                    data-testid="add-linea"
                    className="text-sm text-[#1B4332] hover:underline"
                  >
                    + Agregar línea
                  </button>
                  <div className="flex items-center gap-3">
                    <span
                      data-testid="asiento-balance-status"
                      className={`text-xs label-caps ${balanced ? "text-[#2D6A4F]" : "text-[#A94438]"}`}
                    >
                      {balanced ? "Cuadrado" : `Diferencia: ${fmtCLP(totalDebito - totalCredito)}`}
                    </span>
                    <button
                      type="submit"
                      disabled={!balanced || !glosa}
                      data-testid="submit-asiento"
                      className="bg-[#1B4332] hover:bg-[#2D6A4F] disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-sm"
                    >
                      Registrar asiento
                    </button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
        ) : asientos.length === 0 ? (
          <div className="p-8 text-center text-[#59645E] text-sm" data-testid="asientos-empty">
            No hay asientos registrados. Crea el primero.
          </div>
        ) : (
          <table className="acc-table" data-testid="asientos-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>N°</th>
                <th style={{ width: 110 }}>Fecha</th>
                <th>Glosa / Detalle</th>
                <th className="num" style={{ width: 130 }}>Débito</th>
                <th className="num" style={{ width: 130 }}>Crédito</th>
                <th style={{ width: 90 }}>Estado</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {asientos.map((a) => (
                <React.Fragment key={a.id}>
                  <tr className="bg-[#F9F8F6]" data-testid={`asiento-row-${a.numero}`}>
                    <td className="num font-semibold">#{a.numero}</td>
                    <td>{fmtDate(a.fecha)}</td>
                    <td className="font-medium">{a.glosa}</td>
                    <td className="num font-semibold">{fmtCLP(a.total_debito)}</td>
                    <td className="num font-semibold">{fmtCLP(a.total_credito)}</td>
                    <td>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tracking-[0.1em] uppercase font-semibold ${
                          a.estado === "registrado"
                            ? "bg-[#2D6A4F]/10 text-[#2D6A4F]"
                            : "bg-[#A94438]/10 text-[#A94438]"
                        }`}
                      >
                        {a.estado}
                      </span>
                    </td>
                    <td>
                      {a.estado === "registrado" && (
                        <button
                          onClick={() => anular(a.id)}
                          data-testid={`anular-asiento-${a.numero}`}
                          className="text-[#A94438] hover:bg-[#A94438]/10 p-1 rounded-sm"
                          title="Anular"
                        >
                          <Ban className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {a.lineas.map((l, idx) => (
                    <tr key={idx} className="text-[#59645E]">
                      <td></td>
                      <td></td>
                      <td>
                        <span className="font-mono text-[11px] text-[#1C211F]">{l.cuenta_codigo}</span>{" "}
                        <span className="text-[12px]">{l.cuenta_nombre}</span>
                        {l.glosa && <span className="text-[11px] text-[#A3ABA6] ml-2">— {l.glosa}</span>}
                      </td>
                      <td className="num text-[12px]">{l.debito > 0 ? fmtCLP(l.debito) : ""}</td>
                      <td className="num text-[12px]">{l.credito > 0 ? fmtCLP(l.credito) : ""}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
